// src/components/BlogPost.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css'; // Dark theme
// Import languages you need
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-markup'; // HTML
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-powershell';
import 'prismjs/components/prism-yaml';
// Add more languages as needed

import '../styles/BlogPost.css';

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    fetch(`/api/notionBlog/${slug}`)
      .then(res => {
        if (!res.ok) throw new Error(`Status ${res.status}`);
        return res.json();
      })
      .then(data => setPost(data))
      .catch(err => {
        console.error('Fetch error (blog post):', err);
        setErrorMsg(
          err.message.includes('Failed to fetch')
            ? 'Cannot reach server (CORS or server down)'
            : err.message.includes('404')
              ? 'Post not found'
              : `Error: ${err.message}`
        );
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Highlight code after post loads
  useEffect(() => {
    if (post) {
      Prism.highlightAll();
    }
  }, [post]);

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  if (loading) return <p className="status">Loading post…</p>;
  if (errorMsg) return (
    <article className="blog-post">
      <p className="status error">{errorMsg}</p>
      <Link to="/">← Back to Posts</Link>
    </article>
  );
  if (!post) return null;

  return (
    <article className="blog-post">
      <Link to="/" className="back-link">← Back to Posts</Link>
      <h1>{post.title}</h1>
      {post.blocks?.map((block, i) => {
        switch (block.type) {
          case 'paragraph':
            return <p key={i}>{block.text}</p>;

          case 'heading_1':
            return <h1 key={i}>{block.text}</h1>;

          case 'heading_2':
            return <h2 key={i}>{block.text}</h2>;

          case 'heading_3':
            return <h3 key={i}>{block.text}</h3>;

          case 'bulleted_list_item':
            return <li key={i}>{block.text}</li>;

          case 'code':
            return (
              <div key={i} className="code-block-wrapper">
                <div className="code-header">
                  <span className="code-language">{block.language || 'plaintext'}</span>
                  <button
                    className="copy-button"
                    onClick={() => copyToClipboard(block.text, i)}
                    aria-label="Copy code"
                  >
                    {copiedIndex === i ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="code-block">
                  <code className={`language-${block.language || 'plaintext'}`}>
                    {block.text}
                  </code>
                </pre>
              </div>
            );

          case 'table':
            return (
              <div key={i} className="table-wrapper">
                <table className="blog-table">
                  {block.hasColumnHeader && block.rows.length > 0 ? (
                    <>
                      <thead>
                        <tr>
                          {block.rows[0].map((cell, cellIndex) => (
                            <th key={cellIndex}>{cell}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {block.rows.slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : (
                    <tbody>
                      {block.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  )}
                </table>
              </div>
            );

          default:
            return null;
        }
      })}
    </article>
  );
}

export default BlogPost;